import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { BodyBadge } from '@/components/BodyBadge';
import { Icon } from '@/components/Icon';
import { HowRecordsAreBuilt } from '@/features/records/HowRecordsAreBuilt';
import { fetchRecords } from '@/lib/api';
import { sortByActivity } from '@/lib/records';
import './Records.css';

/**
 * Legislative History — the list.
 *
 * Sorted by most recent activity, and that is the only sort. There is deliberately no
 * user-facing sort switcher; adding one means revisiting that decision, not just adding a
 * control.
 *
 * The list is short on purpose. It holds only records that exist because one of this user's
 * Signals matched, or because they tracked a number by hand — never a corpus-wide listing.
 */
export function RecordsList() {
  const { data, isLoading } = useQuery({ queryKey: ['records'], queryFn: fetchRecords });
  const [howOpen, setHowOpen] = useState(false);

  const records = sortByActivity(data ?? []);

  return (
    <div className="records">
      <header className="records__head">
        <div>
          <h1 className="records__title">Legislative History</h1>
          <p className="records__subtitle">
            One ordinance or resolution, followed across every meeting that touched it.
          </p>
        </div>
        <button type="button" className="records__how" onClick={() => setHowOpen(true)}>
          How records are built
        </button>
      </header>

      {isLoading && <p className="records__empty">Loading…</p>}

      {!isLoading && records.length === 0 && (
        <div className="records__empty">
          <p>No records yet.</p>
          <p className="records__emptyHint">
            A record appears when two or more meetings in the same town discuss the same
            ordinance number — and one of your Signals caught it. You can also track a number
            by hand from any recap that carries one.
          </p>
        </div>
      )}

      <ul className="records__list">
        {records.map((record) => (
          <li key={record.id}>
            <Link
              to="/legislative-history/$recordId"
              params={{ recordId: record.id }}
              className="records__row"
            >
              <span className="records__rowMain">
                <span className="records__num">
                  {record.num ?? <em className="records__noNum">No number yet</em>}
                </span>
                <span className="records__label">{record.label}</span>
                {/* One line of plain English — the whole point of the row. */}
                <span className="records__status">{record.status}</span>
              </span>

              <span className="records__rowMeta">
                <span className="records__where">
                  <BodyBadge body={record.body} short />
                  {record.townName}, {record.townRegion}
                </span>
                <span className="records__stage">{record.stage}</span>
                <span className="records__span">
                  {record.meetingCount} meetings · {record.firstSeen} – {record.lastSeen}
                </span>
                {/* Provenance: which Signal produced this, or that the user added it. */}
                <span className="records__source">
                  <Icon name={record.signalName ? 'signals' : 'plus'} />
                  {record.signalName ?? 'Added by you'}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>

      {howOpen && <HowRecordsAreBuilt onClose={() => setHowOpen(false)} />}
    </div>
  );
}
